import {
  SIM_VERSION,
  checkPlanArmy,
  simulate,
  validateLayout,
  validatePlan,
  type AttackPlan,
  type BattleResult,
  type Layout,
  type UnitCounts,
  type UnitKind,
} from "@empire/core-sim";
import { RESOURCE_KINDS, emptyResources, type Resources } from "./economy.js";
import { layoutFromPicture } from "./picture.js";

/**
 * Trials (29 Sep 2026): hand-made battles to play any time.
 *
 * Playtest finding #9: a new village runs out of things to do after one
 * building. Clash of Clans answers that with a single-player campaign that is
 * always there; these are ours, and they teach the game's actual subject —
 * which towers stop which units, and how order and timing break a maze.
 *
 * Two kinds:
 *  - attack: a fixed maze and a fixed army; the player plans the waves.
 *  - defend: a fixed attack and an empty board with the gate placed; the
 *    player builds the maze within the trial's Wall and Guild.
 *
 * Everything here is data plus one pure scoring function, shared by the client
 * (which plays the replay at once) and the server (which re-runs the same
 * simulation before paying anything). A test proves every trial is winnable
 * for three stars and that a careless attempt is not.
 */

export type TrialKind = "attack" | "defend";

interface TrialBase {
  readonly id: string;
  readonly kind: TrialKind;
  readonly name: string;
  /** One line: what this trial teaches. Shown before playing. */
  readonly lesson: string;
  readonly wallLevel: number;
  readonly engineersGuildLevel: number;
  /** Paid the FIRST time each star is earned: index 0 = ★, 1 = ★★, 2 = ★★★. */
  readonly rewards: readonly [number, number, number];
}

export interface AttackTrial extends TrialBase {
  readonly kind: "attack";
  readonly picture: readonly string[];
  readonly army: UnitCounts;
  /** ★ = break in. ★★ = lose at most `lost2`. ★★★ = lose at most `lost3`. */
  readonly lost2: number;
  readonly lost3: number;
}

export interface DefendTrial extends TrialBase {
  readonly kind: "defend";
  /** The empty board: gate and core only. */
  readonly picture: readonly string[];
  readonly plan: AttackPlan;
  /** ★★★ = nobody reaches the core. ★★ = at most `reached2`. ★ = at most `reached1`. */
  readonly reached2: number;
  readonly reached1: number;
}

export type Trial = AttackTrial | DefendTrial;

const EMPTY_ROW = "............";
const board = (top: readonly string[]): string[] => {
  const rows = [...top];
  while (rows.length < 12) rows.push(EMPTY_ROW);
  return rows;
};

const units = (kind: UnitKind, n: number): UnitKind[] => Array.from({ length: n }, () => kind);

/** West gate on row 3, the core in the middle. The board every defend trial starts from. */
const WEST_GATE = board([
  EMPTY_ROW,
  EMPTY_ROW,
  EMPTY_ROW,
  "G...........",
  EMPTY_ROW,
  ".....CC.....",
  ".....CC.....",
]);

export const TRIALS: readonly Trial[] = [
  {
    id: "open-gate",
    kind: "attack",
    name: "The Open Gate",
    lesson: "Two Arrow towers guard a short road. Get through, lose little.",
    wallLevel: 0,
    engineersGuildLevel: 0,
    picture: board([
      ".....G......",
      ".....#......",
      "....a#......",
      ".....#a.....",
      ".....#......",
      ".....CC.....",
      ".....CC.....",
    ]),
    army: { militia: 8 },
    lost2: 5,
    lost3: 3,
    rewards: [100, 100, 150],
  },
  {
    id: "first-maze",
    kind: "defend",
    name: "First Maze",
    lesson: "Ten raiders. A longer road keeps them under fire longer.",
    wallLevel: 0,
    engineersGuildLevel: 0,
    picture: WEST_GATE,
    plan: {
      waves: [
        { offsetTicks: 0, units: units("militia", 5) },
        { offsetTicks: 30, units: units("militia", 5) },
      ],
    },
    reached2: 2,
    reached1: 5,
    rewards: [100, 100, 150],
  },
  {
    id: "shield-first",
    kind: "attack",
    name: "Shields First",
    lesson: "Send the Shieldbearers ahead so the Militia walk in behind them.",
    wallLevel: 0,
    engineersGuildLevel: 0,
    picture: board([
      "............",
      "G#####......",
      "..a..#......",
      ".....#a.....",
      "....b#......",
      ".....CC.....",
      ".....CC.....",
    ]),
    army: { militia: 8, shieldbearer: 2 },
    lost2: 3,
    lost3: 1,
    rewards: [150, 150, 200],
  },
  {
    id: "burn-the-crowd",
    kind: "defend",
    name: "Burn the Crowd",
    lesson: "Twelve men in one tight pack. The Brazier burns groups.",
    wallLevel: 0,
    engineersGuildLevel: 0,
    picture: WEST_GATE,
    plan: { waves: [{ offsetTicks: 0, units: units("militia", 12) }] },
    reached2: 2,
    reached1: 5,
    rewards: [150, 150, 200],
  },
  {
    id: "draw-fire",
    kind: "attack",
    name: "Draw Their Fire",
    lesson: "Towers fire at whoever comes first. Send one man ahead to draw the fire.",
    wallLevel: 0,
    engineersGuildLevel: 0,
    picture: board([
      "............",
      "............",
      "G####.......",
      "..b.#.......",
      "....#b......",
      "....#CC.....",
      ".....CC.....",
    ]),
    army: { militia: 12 },
    lost2: 3,
    lost3: 1,
    rewards: [150, 200, 250],
  },
  {
    id: "riders",
    kind: "defend",
    name: "Riders at the Gate",
    lesson: "Riders are fast. Make them walk — every bend is time under fire.",
    wallLevel: 2,
    engineersGuildLevel: 3,
    picture: WEST_GATE,
    plan: { waves: [{ offsetTicks: 0, units: units("rider", 5) }] },
    reached2: 1,
    reached1: 3,
    rewards: [200, 200, 300],
  },
  {
    id: "the-ram",
    kind: "attack",
    name: "The Barricade",
    lesson: "A barricade stops everyone. The Ram breaks it — send it first.",
    wallLevel: 1,
    engineersGuildLevel: 0,
    picture: board([
      ".....G......",
      ".....#......",
      "....a#......",
      ".....B......",
      "....b#a.....",
      ".....CC.....",
      ".....CC.....",
    ]),
    army: { ram: 1, militia: 10 },
    lost2: 8,
    lost3: 5,
    rewards: [200, 250, 300],
  },
  {
    id: "armour",
    kind: "defend",
    name: "Armour",
    lesson: "Shieldbearers shrug off arrows. The Ballista pierces armour.",
    wallLevel: 2,
    engineersGuildLevel: 4,
    picture: WEST_GATE,
    plan: {
      waves: [
        { offsetTicks: 0, units: units("shieldbearer", 4) },
        { offsetTicks: 20, units: units("militia", 8) },
      ],
    },
    reached2: 2,
    reached1: 4,
    rewards: [250, 250, 350],
  },
  {
    id: "recovery",
    kind: "attack",
    name: "Rebuild Your Army",
    lesson:
      "Break through with borrowed troops. Once in your first 7 days, after a real loss, win 10 Militia if standing and queued supply is below 5. Otherwise practise for free.",
    wallLevel: 0,
    engineersGuildLevel: 0,
    picture: board([
      ".....G......",
      ".....#......",
      "....a#......",
      ".....#......",
      ".....#......",
      ".....CC.....",
      ".....CC.....",
    ]),
    army: { militia: 10 },
    lost2: 5,
    lost3: 3,
    rewards: [0, 0, 0],
  },
];

export function trialById(id: string): Trial | undefined {
  return TRIALS.find((t) => t.id === id);
}

/** The maze an attack trial is fought against, or the empty board a defend trial starts from. */
export function trialLayout(trial: Trial): Layout {
  return layoutFromPicture(
    trial.picture,
    { wallLevel: trial.wallLevel, engineersGuildLevel: trial.engineersGuildLevel },
    trial.id
  );
}

export type TrialSubmission = { plan: AttackPlan } | { layout: Layout };

export type TrialScore =
  | { ok: true; stars: 0 | 1 | 2 | 3; result: BattleResult; layout: Layout; plan: AttackPlan }
  | { ok: false; message: string };

/** Stars for a finished battle. Pure. */
export function starsFor(trial: Trial, result: BattleResult): 0 | 1 | 2 | 3 {
  if (trial.kind === "attack") {
    if (result.outcome !== "breach") return 0;
    const lost = result.army.lost;
    return lost <= trial.lost3 ? 3 : lost <= trial.lost2 ? 2 : 1;
  }
  const reached = result.army.reached;
  return reached === 0 ? 3 : reached <= trial.reached2 ? 2 : reached <= trial.reached1 ? 1 : 0;
}

/**
 * Play a trial. The server calls exactly this before paying; the client calls
 * it to show the result straight away. Refuses anything the trial does not
 * allow — an army it did not hand out, a maze past its budget, a moved gate,
 * an upgraded tower.
 */
export function playTrial(trial: Trial, submission: TrialSubmission): TrialScore {
  if (trial.kind === "attack") {
    if (!("plan" in submission)) return { ok: false, message: "This trial needs an attack plan." };
    const plan = submission.plan;
    const shape = validatePlan(plan);
    if (!shape.ok) return { ok: false, message: shape.error.message };
    const held = checkPlanArmy(plan, trial.army);
    if (!held.ok) return { ok: false, message: held.error.message };
    const layout = trialLayout(trial);
    const result = simulate({ simVersion: SIM_VERSION, layout, plan });
    return { ok: true, stars: starsFor(trial, result), result, layout, plan };
  }

  if (!("layout" in submission)) return { ok: false, message: "This trial needs a defence." };
  const start = trialLayout(trial);
  const layout: Layout = {
    ...submission.layout,
    wallLevel: trial.wallLevel,
    engineersGuildLevel: trial.engineersGuildLevel,
  };
  if (layout.gate.x !== start.gate.x || layout.gate.y !== start.gate.y) {
    return { ok: false, message: "The gate stays where it is in a trial." };
  }
  if (layout.tiles.some((t) => t.tower && t.tower.level !== 1)) {
    return { ok: false, message: "Towers stay at level 1 in a trial." };
  }
  const legal = validateLayout(layout);
  if (!legal.ok) return { ok: false, message: legal.error.message };
  const result = simulate({ simVersion: SIM_VERSION, layout, plan: trial.plan });
  return { ok: true, stars: starsFor(trial, result), result, layout, plan: trial.plan };
}

/** What going from `before` to `after` stars pays: each star tier once, ever. */
export function trialReward(trial: Trial, before: number, after: number): Resources {
  const out = emptyResources();
  for (let tier = Math.max(0, before); tier < Math.min(3, after); tier++) {
    for (const kind of RESOURCE_KINDS) out[kind] += trial.rewards[tier]!;
  }
  return out;
}
